import React from 'react';
import {
  Card,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
} from 'reactstrap';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import AppCheckboxList from 'components/App/AppCheckboxList';
import AppFlatPickInput from 'components/App/FlatPickInput';
import AppMeta from 'components/App/Meta';
import FormField from 'components/AddLocation/FormField';

import DATES_OF_WEEK from 'constants/datesOfWeek';
import { LOCATIONS, LOGIN_TYPE, ROOM_STATUS, storageKey, themeColor } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { PARAM_SETTING } from 'constants/setting';
import { updateCloseOpenRoom } from 'utils/validate';

import styles from './OpenCloseRoom.module.scss';

const breadcrumbList = [
  {
    name: 'Giá và tình trạng phòng trống',
    isActive: false,
    link: LOCATIONS.LOCATION_LIST,
  },
  {
    name: 'Mở/Đóng phòng',
    isActive: true,
    link: '',
  },
];

const newDate = new Date().setHours(0, 0, 0, 0);
const aWeekAgo = new Date(moment(newDate).add(7, 'days'));

function OpenCloseRoom(props) {
  const {
    isLoadingUpdateCloseOpenRoom,
    setting,
    roomOfPlaceList,
    actionGetListRoomsOfPlace,
    actionUpdateCloseOpenRoom,
  } = props;

  const location = useLocation();
  const history = useHistory();
  const placeId = new URLSearchParams(location.search).get('place');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const validation = useFormik({
    initialValues: {
      startDate: newDate,
      endDate: aWeekAgo,
      weekDays: DATES_OF_WEEK,
      rooms: [],
      status: ROOM_STATUS.AVAILABLE,
    },
    validationSchema: Yup.object(updateCloseOpenRoom),
    onSubmit: (values) => {
      const data = {
        startDate: new Date(values.startDate),
        endDate: new Date(values.endDate.setUTCHours(23, 59, 59)),
        weekDays: values.weekDays
          .filter((item) => item.isChecked)
          .map((item) => +item.value),
        rooms: values.rooms
          .filter((item) => item.isChecked)
          .map((item) => item._id),
        status: values.status,
      };

      actionUpdateCloseOpenRoom({
        placeId,
        data,
      });
    },
  });

  const onChangeStartDate = React.useCallback((date) => {
    if (date.length === 0) {
      validation.setValues((prevState) => ({
        ...prevState,
        startDate: '',
      }));
    } else {
      const checkDateEnd = date[0] > moment(validation.values.endDate).subtract(7, 'days');
      if (checkDateEnd) {
        validation.setValues((prevState) => ({
          ...prevState,
          endDate: '',
        }));
      }

      validation.setValues((prevState) => ({
        ...prevState,
        startDate: date[0],
      }));
    }
  }, [validation.values.endDate]);

  const onChangeEndDate = React.useCallback((date) => {
    validation.setValues((prevState) => ({
      ...prevState,
      endDate: date.length > 0 ? date[0] : '',
    }));
  }, []);

  const onToggleCheckedDateInWeek = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      weekDays: values.weekDays.map((item) => {
        if (item.value === +e.target.value) {
          return {
            ...item,
            isChecked: e.target.checked,
          };
        }
        return item;
      }),
    }));
  }, []);

  const onToggleSelectAllDate = React.useCallback((e) => {
    validation.setFieldValue('weekDays', validation.values.weekDays.map((item) => ({
      ...item,
      isChecked: +e.target.value === 1,
    })));
  }, [validation.values]);

  const onToggleCheckedRoom = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      rooms: values.rooms.map((item) => {
        if (item._id === e.target.originalItem._id) {
          return {
            ...item,
            isChecked: e.target.checked,
          };
        }
        return item;
      }),
    }));
  }, []);

  const onCheckedAllRooms = React.useCallback((e) => {
    validation.setFieldValue('rooms', validation.values.rooms.map((item) => ({
      ...item,
      isChecked: +e.target.value === 1,
    })));
  }, [validation.values]);

  const onChangeStatus = React.useCallback((e) => {
    validation.setValues((values) => ({
      ...values,
      status: e.target.value,
    }));
  }, []);

  const onCancelUpdateRoom = React.useCallback(() => {
    history.push(`${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`);
  }, []);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
    }
  }, [placeId]);

  React.useEffect(() => {
    if (!isEmpty(setting[PARAM_SETTING.ROOM_NAMES]) && !isEmpty(roomOfPlaceList)) {
      const mergedRoomList = setting[PARAM_SETTING.ROOM_NAMES].map((item) => item.children).flat();

      validation.setFieldValue('rooms', roomOfPlaceList.map((room) => {
        const roomNameObj = mergedRoomList.reduce((acc, item) => ({
          ...acc,
          [room.roomName]: item.vi,
        }), {});

        return ({
          _id: room._id,
          name: room.name || roomNameObj[room.roomName],
          roomName: room.roomName,
          roomNameLabel: roomNameObj[room.roomName],
          isChecked: false,
        });
      }));
    }
  }, [roomOfPlaceList, setting]);

  return (
    <div className={classNames('page-content', styles.Page)}>
      <AppMeta
        name="Mở / Đóng phòng"
      />

      <Container fluid>
        <AppBreadcrumb
          pageTitle="Mở / Đóng phòng"
          breadcrumbList={breadcrumbList}
        />

        <Card className="Section">
          <CardTitle className="Section__Title">
            Quý vị muốn thay đổi ngày nào?
          </CardTitle>

          <Row>
            <Col md="6" className="mb-24">
              <FormField label="Từ ngày">
                <AppFlatPickInput
                  name="startDate"
                  validation={validation}
                  onChange={onChangeStartDate}
                  options={{
                    minDate: 'today',
                  }}
                />
              </FormField>
            </Col>

            <Col md="6" className="mb-24">
              <FormField label="Đến ngày">
                <AppFlatPickInput
                  name="endDate"
                  validation={validation}
                  onChange={onChangeEndDate}
                  options={{
                    minDate: moment(validation.values.startDate || newDate).toDate(),
                  }}
                />
              </FormField>
            </Col>
          </Row>

          <AppCheckboxList
            name="weekDays"
            list={validation.values.weekDays}
            validation={validation}
            onChecked={onToggleCheckedDateInWeek}
            onToggleCheckedAll={onToggleSelectAllDate}
          />
        </Card>

        <Card className="Section">
          <CardTitle className="Section__Title">
            Quý vị muốn mở hay đóng những phòng nào?
          </CardTitle>

          <AppCheckboxList
            isVertical
            name="rooms"
            list={validation.values.rooms}
            validation={validation}
            onChecked={onToggleCheckedRoom}
            onToggleCheckedAll={onCheckedAllRooms}
          />
        </Card>

        <Card className="Section mb-4">
          <CardTitle className="Section__Title">
            Chọn tình trạng cho những phòng đã chọn:
          </CardTitle>

          <div className={styles.RoomStatus}>
            <label htmlFor={ROOM_STATUS.AVAILABLE}>
              <Input
                id={ROOM_STATUS.AVAILABLE}
                name="status"
                value={ROOM_STATUS.AVAILABLE}
                type="radio"
                validation={validation}
                onChange={onChangeStatus}
              />
              <span>Mở</span>
            </label>

            <label htmlFor={ROOM_STATUS.CLOSE}>
              <Input
                id={ROOM_STATUS.CLOSE}
                name="status"
                value={ROOM_STATUS.CLOSE}
                type="radio"
                validation={validation}
                onChange={onChangeStatus}
              />
              <span>Đóng</span>
            </label>
          </div>
        </Card>

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <AppButton color={themeColor.SECONDARY} onClick={onCancelUpdateRoom}>
              Hủy
            </AppButton>

            <AppButton
              loading={isLoadingUpdateCloseOpenRoom}
              onClick={validation.handleSubmit}
            >
              Cập nhật
            </AppButton>
          </div>
        </div>
      </Container>
    </div>
  );
}

OpenCloseRoom.propTypes = {
  isLoadingUpdateCloseOpenRoom: PropTypes.bool.isRequired,
  setting: PropTypes.instanceOf(Object),
  roomOfPlaceList: PropTypes.instanceOf(Array),
  actionGetListRoomsOfPlace: PropTypes.func,
  actionUpdateCloseOpenRoom: PropTypes.func,
};

OpenCloseRoom.defaultProps = {
  setting: INITIAL_VALUE.OBJECT,
  roomOfPlaceList: INITIAL_VALUE.ARRAY,
  actionGetListRoomsOfPlace: INITIAL_VALUE.FUNCTION,
  actionUpdateCloseOpenRoom: INITIAL_VALUE.FUNCTION,
};

export default OpenCloseRoom;
